import { FC, useEffect, useState } from "react";
import "./MembershipPlanDetailScreen.css";
import backIcon from "../../../assets/images/back-icon.svg";
import planTickIcon from "../../../assets/images/membership-plan-tick-icon.svg";
import letsDoItIcon from "../../../assets/images/lets-do-it-icon.svg";
import offerAppliedIcon from "../../../assets/images/membership-offer-applied-icon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTE_PATHS from "utils/routePaths";
import { MEMBERSHIP_PLAN_TYPE, PROFILE_STATUS } from "utils/constants";
import { getMembershipCardImage } from "utils/utils";
import { membershipPlanTypes } from "utils/utils";
import { STORAGE_KEY, WEB_LINK } from "utils/strings";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import { useStorage } from "services/StorageProvider";
import { trackMembershipApplied } from "utils/analytics";

const MembershipPlanDetailScreen: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfileData } = useStorage();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedMembershipData, setSelectedMembershipData] =
    useState<any>(null);
  const [selectedPlanData, setSelectedPlanData] = useState<any>(null);
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);

  useEffect(() => {
    const storedData = localStorage.getItem(
      STORAGE_KEY.SELECTED_MEMBERSHIP_DATA,
    );
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData?.plans && Array.isArray(parsedData.plans)) {
        parsedData.plans.sort((a: any, b: any) => a.validity - b.validity);
      }
      setSelectedMembershipData(parsedData);
      setSelectedPlanData(parsedData?.plans[selectedTab]);
    }
  }, [selectedTab]);

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    setSelectedPlanData(selectedMembershipData?.plans[index]);
  };

  const handleJoinCircleClick = () => {
    trackMembershipApplied();
    localStorage.setItem(
      STORAGE_KEY.SELECTED_MEMBERSHIP_PLAN_DATA,
      JSON.stringify(selectedPlanData),
    );
    if (userProfileData?.credit?.status === PROFILE_STATUS.NON_MEMBER) {
      navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
        state: {
          anyData: {
            title: "Step into Sabe",
            url: WEB_LINK.BECOME_MEMBER,
          },
        },
      });
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.UNDER_REVIEW
    ) {
      setShowBecomeMemberDialog(true);
    } else {
      navigate(ROUTE_PATHS.WSM_MEMBERSHIP_PAYMENT_DETAIL);
    }
  };

  const handleEventCardClick = (index: number) => {
    if (index === 0) {
      navigate(ROUTE_PATHS.WSM_CALENDAR_DF_WORKSPACE_DETAIL, {
        state: { from: location.pathname },
      });
    } else if (index === 1) {
      navigate(ROUTE_PATHS.WSM_CALENDAR_MB_STUDIO_DETAIL, {
        state: { from: location.pathname },
      });
    } else if (index === 2) {
      navigate(ROUTE_PATHS.WSM_CALENDAR_EVENT_DETAIL, {
        state: { from: location.pathname },
      });
    }
  };

  const getPerDayPrice = () => {
    const planPrice = selectedPlanData?.discounted_price
      ? selectedPlanData?.discounted_price
      : selectedPlanData?.plan_price;
    const planDays =
      (selectedPlanData?.validity + selectedPlanData?.complimentary_validity) *
      30;
    return Math.round(planPrice / planDays);
  };

  return (
    <div className="wms-mobile-container">
      <div className="membership-plan-detail-screen-container">
        <div className="membership-plan-detail-header-container">
          <img
            src={backIcon}
            alt="dropdown-logo"
            className="image"
            onClick={handleBackButtonClick}
          />
          <div className="title">{`${selectedMembershipData?.membership_type} Membership`}</div>
        </div>
        {selectedMembershipData?.plans &&
          selectedMembershipData?.plans?.length > 1 && (
            <div className="membership-plan-detail-tab-bar-background-container">
              <div className="membership-plan-detail-tab-bar-container">
                {selectedMembershipData?.plans?.map(
                  (plan: any, index: number) => (
                    <div
                      key={index}
                      className={`membership-plan-detail-tab-bar-item-container ${
                        selectedTab === index && "selected"
                      }`}
                      onClick={() => handleTabChange(index)}
                    >
                      {`${
                        MEMBERSHIP_PLAN_TYPE.find(
                          (type) => type.value === plan?.plan_type,
                        )?.name
                      } ${
                        plan?.complimentary_validity > 0
                          ? `+ ${plan?.complimentary_validity}M`
                          : ""
                      }`}
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        <div className="membership-plan-detail-card-container">
          <div className="image-text-container">
            <img
              className="card-image"
              src={getMembershipCardImage(
                selectedMembershipData?.membership_type,
              )}
              alt="membership-card"
            />
            <div className="custom-text-overlay"></div>
          </div>
          <div className="right-container">
            <div className="sub-container">
              <div className="plan-amount-container">
                {`INR ${
                  selectedPlanData?.discounted_price
                    ? selectedPlanData?.discounted_price?.toLocaleString(
                        "en-US",
                      )
                    : selectedPlanData?.plan_price?.toLocaleString("en-US")
                }`}
                <span>Only</span>
              </div>
              {selectedPlanData?.discounted_price && (
                <div className="plan-offer-price-container">
                  {`INR ${selectedPlanData?.plan_price?.toLocaleString(
                    "en-US",
                  )}`}
                </div>
              )}
              <div className="total-credit-text">{`${selectedPlanData?.total_credits} Credits`}</div>
            </div>
            <div className="sub-container">
              <div className="valid-up-to-text">Valid Up to</div>
              <div className="credit-validity-container">
                {`${selectedPlanData?.validity}${
                  selectedPlanData?.complimentary_validity > 0
                    ? ` + ${selectedPlanData?.complimentary_validity} Months`
                    : " Months"
                }`}
              </div>
            </div>
          </div>
        </div>
        {membershipPlanTypes.map((data: any, index: number) => (
          <div
            key={index}
            className="membership-plan-credit-details-container"
            onClick={() => handleEventCardClick(index)}
          >
            <div className="sub-container">
              <div style={{ width: "100%", display: "flex" }}>
                <img src={planTickIcon} alt="plan-tick-icon" />
                <span>
                  {data.title}
                  <span style={{ color: "#C2B58C" }}> {data.subTitle}</span>
                </span>
              </div>
              {data.creditType && (
                <div className="credit_container">
                  <div className="title">{`${
                    selectedPlanData?.[data.creditType]
                  }`}</div>
                  <div className="sub-title">Hours</div>
                </div>
              )}
            </div>
          </div>
        ))}
        {userProfileData?.credit?.status === PROFILE_STATUS.PAYMENT_PENDING ||
        userProfileData?.credit?.status === PROFILE_STATUS.MEMBER ? (
          <div className="membership-plan-detail-screen-footer-container">
            {selectedPlanData?.discounted_price && (
              <div className="membership-plan-detail-screen-footer-tooltip-container">
                <div className="offer-container">
                  <img src={offerAppliedIcon} alt="dropdown-logo" />
                </div>

                <div className="membership-plan-detail-screen-footer-tooltip-text">
                  <span className="first-text">
                    {
                      MEMBERSHIP_PLAN_TYPE.find(
                        (type) => type.value === selectedPlanData?.plan_type,
                      )?.name
                    }{" "}
                  </span>
                  <span className="second-text">You Just</span>
                  <span className="third-text">
                    Saved{" "}
                    {(
                      selectedPlanData?.plan_price -
                      selectedPlanData?.discounted_price
                    )?.toLocaleString("en-US")}{" "}
                    INR
                  </span>
                </div>
              </div>
            )}
            <div className="button-container" onClick={handleJoinCircleClick}>
              <div className="left-container">
                <div className="text-container">
                  <span>
                    {selectedPlanData?.discounted_price
                      ? selectedPlanData?.discounted_price?.toLocaleString(
                          "en-US",
                        )
                      : selectedPlanData?.plan_price?.toLocaleString(
                          "en-US",
                        )}{" "}
                    INR
                  </span>
                  <span className="sub-title">
                    {getPerDayPrice()?.toLocaleString("en-US")} INR /day
                  </span>
                </div>
              </div>
              <div className="divider"></div>
              <div className="right-container">
                <span>Choose Plan</span>
                <img src={letsDoItIcon} alt="lets-do-it-logo" />
              </div>
            </div>
          </div>
        ) : (
          <div className="membership-plan-detail-screen-footer-container">
            {selectedPlanData?.discounted_price && (
              <div className="membership-plan-detail-screen-footer-tooltip-container">
                <div className="offer-container">
                  <img src={offerAppliedIcon} alt="dropdown-logo" />
                </div>

                <div className="membership-plan-detail-screen-footer-tooltip-text">
                  <span className="first-text">
                    {
                      MEMBERSHIP_PLAN_TYPE.find(
                        (type) => type.value === selectedPlanData?.plan_type,
                      )?.name
                    }{" "}
                  </span>
                  <span className="second-text">You Just</span>
                  <span className="third-text">
                    Saved{" "}
                    {(
                      selectedPlanData?.plan_price -
                      selectedPlanData?.discounted_price
                    )?.toLocaleString("en-US")}{" "}
                    INR
                  </span>
                </div>
              </div>
            )}
            <div className="button-container" onClick={handleJoinCircleClick}>
              <div className="non-membership-button">
                <span>Join Circle</span>
                <img src={letsDoItIcon} alt="lets-do-it-logo" />
              </div>
            </div>
          </div>
        )}
      </div>
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
    </div>
  );
};

export default MembershipPlanDetailScreen;
